import { Compass, HeartHandshake, MapPinned } from "lucide-react";
import { IMAGES } from "@/data/site";
import PageHero from "@/components/PageHero";
import { Accent, Reveal, SectionHead } from "@/components/Reveal";

const VALUES = [
  { icon: Compass, title: "Craft over catalogues", text: "We'd rather design one perfect route than sell a hundred generic ones." },
  { icon: HeartHandshake, title: "People first", text: "Drivers paid fairly, local guides employed, travellers treated like family." },
  { icon: MapPinned, title: "Roads we know", text: "We only sell journeys our own team has driven, walked or sailed." },
];

const TEAM = [
  { name: "Arjun Orego", role: "Founder & Chief Route Designer", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80" },
  { name: "Sana Kapoor", role: "Head of India Operations", img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80" },
  { name: "David Mathew", role: "International Partnerships", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=80" },
  { name: "Ritu Verma", role: "24×7 Traveller Support Lead", img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&q=80" },
];

const About = () => (
  <main data-testid="about-page">
    <PageHero
      image={IMAGES.about}
      eyebrow="Our Story"
      title={<>Born on a highway, <Accent>raised</Accent> by travellers</>}
    />

    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <SectionHead
          eyebrow="Since 2012"
          title={<>It started with one car and a <Accent>promise</Accent></>}
        />
        <div className="mt-8 space-y-5 text-base text-[#F5F5F0]/70 leading-relaxed">
          <p>
            Orego Tours began as a single sedan running airport transfers out of Kochi. Our founder
            made a simple promise to every passenger: the car arrives early, the driver knows the way,
            and someone always answers the phone.
          </p>
          <p>
            Fourteen years later, that promise powers a full travel house — bespoke India tours, a
            chauffeur fleet that has crossed every state border, and international holidays across
            twenty countries. The car count changed. The promise didn't.
          </p>
        </div>
      </div>
      <Reveal delay={0.15}>
        <div className="relative rounded-3xl overflow-hidden h-[420px] border border-white/10">
          <img src={IMAGES.mountains} alt="Himalayan peaks" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/70 to-transparent" />
          <div className="absolute bottom-6 left-6 pill-badge">Himalayas — our favourite office</div>
        </div>
      </Reveal>
    </section>

    <section className="bg-[#0D131B] border-y border-white/10 py-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          align="center"
          eyebrow="What We Believe"
          title={<>Three rules we never <Accent>break</Accent></>}
        />
        <div className="grid sm:grid-cols-3 gap-6 mt-12">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div className="glass rounded-3xl p-8 h-full">
                <span className="w-11 h-11 rounded-2xl bg-[#F2C230]/15 flex items-center justify-center text-[#F2C230]">
                  <v.icon size={19} />
                </span>
                <h3 className="font-sora text-lg font-semibold mt-5">{v.title}</h3>
                <p className="text-sm text-[#F5F5F0]/60 mt-2 leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <SectionHead
        eyebrow="The Team"
        title={<>The people behind your <Accent>journey</Accent></>}
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
        {TEAM.map((m, i) => (
          <Reveal key={m.name} delay={i * 0.08}>
            <div className="group">
              <div className="rounded-3xl overflow-hidden border border-white/10 aspect-[3/4]">
                <img
                  src={m.img}
                  alt={m.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-108 group-hover:scale-110"
                />
              </div>
              <h3 className="font-sora text-base font-semibold mt-4">{m.name}</h3>
              <p className="text-xs text-[#3FB6D3] mt-1 tracking-wide">{m.role}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  </main>
);

export default About;
